import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useInvalidateClientDetails, useInvalidateProposalInformation } from 'bff';
import { useApiClient } from 'bff/ApiClientProvider';
import {
  IndividualDetailsGetRequest,
  IndividualDetailsPostRequest,
  Result,
  ResultStates
} from 'bff/client';
import { UseDisplayErrorMutationOptions } from 'bff/types/display-error-props.type';
import { secondsToMilliseconds } from 'date-fns';
import { useInvalidateInvestmentDetails } from '../../quotes/InvestmentDetailsApi';

export const individualDetailsKeys = {
  base: () => ['individual-details'] as const,
  fetch: (request: IndividualDetailsGetRequest) =>
    [...individualDetailsKeys.base(), request] as const,
  submit: () => [...individualDetailsKeys.base(), 'submit'] as const
};

export const useInvalidateIndividualDetails = () => {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: individualDetailsKeys.base(),
      refetchType: 'all'
    });
};

export const getIndividualPersonalDetailsOptions = (
  getPersonalDetailsViewRequest: IndividualDetailsGetRequest
) => {
  const { individualDetailsApi } = useApiClient();
  return queryOptions({
    staleTime: secondsToMilliseconds(30),
    queryKey: individualDetailsKeys.fetch(getPersonalDetailsViewRequest),
    queryFn: () => individualDetailsApi.individualDetailsGet(getPersonalDetailsViewRequest),
    select: (response) => response.data!
  });
};

export const useSaveIndividualPersonalDetails = (
  options?: UseDisplayErrorMutationOptions<Result, IndividualDetailsPostRequest>
) => {
  const { individualDetailsApi } = useApiClient();
  const queryClient = useQueryClient();
  const invalidateClientDetails = useInvalidateClientDetails();
  const invalidateIndividualDetails = useInvalidateIndividualDetails();
  const invalidateProposalInformation = useInvalidateProposalInformation();
  const invalidateInvestmentDetails = useInvalidateInvestmentDetails();

  return useMutation({
    mutationKey: individualDetailsKeys.submit(),
    mutationFn: (request: IndividualDetailsPostRequest) =>
      individualDetailsApi.individualDetailsPost(request),
    onSuccess: async (data, variables, ctx) => {
      if (data.resultState === ResultStates.DisplayError) {
        options?.onDisplayError?.(data);
      } else {
        await Promise.allSettled([
          invalidateInvestmentDetails(),
          invalidateIndividualDetails(),
          queryClient.invalidateQueries({
            queryKey: individualDetailsKeys.base(),
            refetchType: 'all'
          }),
          invalidateClientDetails(),
          invalidateProposalInformation()
        ]);
        options?.onSuccess?.(data, variables, ctx);
      }
    }
  });
};
